import { ApiClient, ApiResponse } from '../client';
import { Wishlist } from '../models';

/**
 * Servicio para gestionar la lista de deseos
 */
export class WishlistService {
  private client: ApiClient;
  
  constructor(client: ApiClient) {
    this.client = client;
  }
  
  /**
   * Obtener la lista de deseos del usuario
   */
  public async getWishlist(): Promise<ApiResponse<Wishlist>> {
    return await this.client.get<Wishlist>('/products/wishlist');
  }
  
  /**
   * Añadir un producto a la lista de deseos
   * @param productId ID del producto a añadir
   */
  public async addToWishlist(productId: string): Promise<ApiResponse<Wishlist>> {
    return await this.client.post<Wishlist>(`/products/wishlist/${productId}`, {});
  }
  
  /**
   * Eliminar un producto de la lista de deseos
   * @param productId ID del producto a eliminar
   */
  public async removeFromWishlist(productId: string): Promise<ApiResponse<Wishlist>> {
    return await this.client.delete<Wishlist>(`/products/wishlist/${productId}`);
  }
  
  /**
   * Verificar si un producto está en la lista de deseos
   * @param productId ID del producto a verificar
   */
  public async checkInWishlist(productId: string): Promise<ApiResponse<{ isInWishlist: boolean }>> {
    return await this.client.get<{ isInWishlist: boolean }>(`/products/wishlist/check/${productId}`);
  }
  
  /**
   * Mover un producto de la lista de deseos al carrito
   * @param productId ID del producto
   * @param quantity Cantidad a añadir al carrito (por defecto 1)
   */
  public async moveToCart(productId: string, quantity: number = 1): Promise<ApiResponse<any>> {
    return await this.client.post<any>(`/products/wishlist/${productId}/move-to-cart`, {
      quantity
    });
  }
  
  /**
   * Verificar si el usuario tiene lista de deseos
   */
  public async hasWishlist(): Promise<boolean> {
    try {
      const response = await this.getWishlist();
      return response.success && !!response.data && response.data.items.length > 0;
    } catch (error) {
      return false;
    }
  }
  
  /**
   * Obtener cantidad de productos en la lista de deseos
   */
  public async getWishlistCount(): Promise<number> {
    try {
      const response = await this.getWishlist();
      return response.success && response.data ? response.data.items.length : 0;
    } catch (error) {
      return 0;
    }
  }
  
  /**
   * Verificar si la lista de deseos está vacía
   */
  public async isWishlistEmpty(): Promise<boolean> {
    try {
      const count = await this.getWishlistCount();
      return count === 0;
    } catch (error) {
      return true;
    }
  }
  
  /**
   * Vaciar por completo la lista de deseos
   */
  public async clearWishlist(): Promise<ApiResponse<Wishlist>> {
    return await this.client.delete<Wishlist>('/products/wishlist');
  }
  
  /**
   * Obtener productos detallados de la lista de deseos
   * (Incluye información completa de cada producto, no solo el ID)
   */
  public async getWishlistProducts(): Promise<ApiResponse<any>> {
    return await this.client.get<any>('/products/wishlist/products');
  }
} 